import React, { useEffect, useState, useContext, useLayoutEffect } from 'react';
import { disableBodyScroll } from 'body-scroll-lock';
import { captureException } from '@sentry/browser';
import {
    IconClose,
    Spacer,
    mediaMixin,
    spaceMixin,
    token,
    TextButton,
    Text,
    IconChevronRightOutline,
    LoadingMask,
} from '@zillow/constellation';
import { useApolloClient } from '@apollo/client';
import { Provider } from 'react-redux';
import styled, { css } from 'styled-components';

import { UniversalAnalyticsContext } from '@zillow/universal-analytics-react';
import { createUserSessionStore, connectToUserSession } from '@zillow/user-session-store';
import { useYieldCallback } from '@zillow/yield-callback';
import { claimHome } from '../utils/claimUtils';
import { trackHdpLinkClick, trackHmimhwReviewLightboxDisplay } from '../utils/gaUtils';
import { getOwnerDashboardUrl } from '../utils/urlUtils';
import ResponsiveLightbox from './ResponsiveLightbox';
import HomeInfo from './HomeInfo';
import HomeValue from './HomeValue';
import SellerLinks from './SellerLinks';
import {
    ExperienceMessage,
    OmpV2WebUpsellCard,
    OmpV2WebEmphasizedUpsellCard,
} from '@zillow/experience-messaging-client';
import type { OmpV2Result as ExperienceOmpV2Result } from '@zillow/experience-messaging-client';
import OmpHomeWorthUpsell, { mapOmpV2ComponentAlias } from './OmpHomeWorthUpsell';
import type { OmpV2Result } from './OmpHomeWorthUpsell';
import ContactFormLightbox from './ContactFormLightBox';
import type {
    Property,
    Viewer,
    AbTests,
    HomeStatus,
    HomeType,
    ListingSubtype,
    ContingentListingType,
} from '../types/@zillow-types/pongo';

const StyledIconClose = styled(IconClose)`
    color: ${token('colors.white')};
    font-size: 18px;
    position: absolute;
    right: ${spaceMixin(2.5)};
    top: ${spaceMixin(2.5)};

    ${mediaMixin(
        'md_lte',
        css`
            top: ${spaceMixin(1.25)};
            right: ${spaceMixin(1.25)};
        `
    )};
`;

const StyledTextButton = styled(TextButton)`
    display: flex;
`;

const StyledIconChevronRightOutline = styled(IconChevronRightOutline)`
    color: ${token('colors.white')};
    position: absolute;
    top: 1px;
`;

interface PropertyTypes extends Property {
    homeStatus: HomeStatus;
    homeType: HomeType;
    listing_sub_type: ListingSubtype;
    contingentListingType: ContingentListingType;
    zpid: number;
    ompV2Message?: OmpV2Result;
}

interface AbTestTypes extends AbTests {
    HMIMHW_ZO_NFS_UPSELL_ONSITE_MESSAGING: string;
}

interface UnwrappedHomeDetailLightboxContentProps {
    loggedIn?: boolean;
    setZpid: (zpid: number | null) => void;
    zatlasEndpoint: string;
    surfaceId: string;
    placementId: string;
    viewer: Viewer;
    property: PropertyTypes;
    abTests: AbTestTypes;
    isLightboxBackgroundLocked: boolean;
    isCMS?: boolean;
    setHmimhwAnalyticsData: (data) => void;
}

export const UnwrappedHomeDetailLightboxContent = ({
    loggedIn,
    setZpid,
    zatlasEndpoint,
    surfaceId,
    placementId,
    property,
    abTests,
    viewer,
    isLightboxBackgroundLocked,
    setHmimhwAnalyticsData,
    isCMS,
}: UnwrappedHomeDetailLightboxContentProps) => {
    const [showContactAgentForm, setShowContactAgentForm] = useState(false);
    const [isClaimInFlight, setIsClaimInFlight] = useState(false);
    const [isSuccessfullyClaimed, setIsSuccessfullyClaimed] = useState(false);
    const { areRequiredDimensionsSet, resetRequiredCustomDimensions } = useContext(
        UniversalAnalyticsContext
    );
    useEffect(() => {
        if (!showContactAgentForm && areRequiredDimensionsSet) {
            trackHmimhwReviewLightboxDisplay(surfaceId, placementId);
        }
    }, [showContactAgentForm, areRequiredDimensionsSet, surfaceId, placementId]);

    const client = useApolloClient();

    useLayoutEffect(() => {
        const scrollTarget = document.querySelector('.scrollTarget');
        if (isLightboxBackgroundLocked) {
            disableBodyScroll(scrollTarget);
        }
    }, [isLightboxBackgroundLocked]);

    useEffect(() => {
        const triggerClaim = async () => {
            if (
                loggedIn &&
                !property.isConfirmedClaimedByCurrentSignedInUser &&
                !property.isVerifiedClaimedByCurrentSignedInUser &&
                property.zpid
            ) {
                setIsClaimInFlight(true);
                try {
                    await claimHome(client, property.zpid, 'HMMHW_LP');
                    setIsSuccessfullyClaimed(true);
                } catch (error) {
                    setIsSuccessfullyClaimed(false);
                    captureException(error);
                } finally {
                    setIsClaimInFlight(false);
                }
            }
        };
        void triggerClaim();
    }, [property.zpid, loggedIn, property, surfaceId, placementId, client]);

    const onHDPLinkClick = useYieldCallback(() => {
        trackHdpLinkClick(surfaceId, placementId);
    });

    if (showContactAgentForm) {
        return (
            <ContactFormLightbox
                property={property}
                viewer={viewer}
                setShowContactAgentForm={setShowContactAgentForm}
                zatlasEndpoint={zatlasEndpoint}
                surfaceId={surfaceId}
                placementId={placementId}
                isLightboxBackgroundLocked={isLightboxBackgroundLocked}
            />
        );
    }

    const mappedOmpV2Message = mapOmpV2ComponentAlias(property.ompV2Message);
    const propertyWithMappedOmp = { ...property, ompV2Message: mappedOmpV2Message };
    const hasOmpV2Message =
        Boolean(mappedOmpV2Message?.placementGroupDecision?.placementDecisions?.length) || false;

    const showOMPUpsell = hasOmpV2Message;

    return (
        <ResponsiveLightbox isLightboxBackgroundLocked={isLightboxBackgroundLocked} isCMS={isCMS}>
            <HomeInfo
                property={property}
                abTests={abTests}
                data-testid="home-info"
                ownerDashboardLink={
                    <LoadingMask loading={isClaimInFlight} iconSize="sm">
                        <TextButton
                            as="a"
                            onClick={onHDPLinkClick}
                            href={getOwnerDashboardUrl(property.zpid, isSuccessfullyClaimed)}
                        >
                            <Text fontType="body" fontColor="white">
                                Claim this home
                                <StyledIconChevronRightOutline />
                            </Text>
                        </TextButton>
                    </LoadingMask>
                }
            />
            <Spacer marginTop="sm" marginBottom="xs">
                <HomeValue zestimate={property.zestimate} data-testid="home-value" />
            </Spacer>
            <Spacer marginTop="sm">
                {showOMPUpsell ? (
                    <OmpHomeWorthUpsell property={propertyWithMappedOmp} />
                ) : (
                    <React.Fragment>
                        {/* empty module to render in order to get DSP event sent */}
                        {property.ompV2Message && (
                            <ExperienceMessage
                                result={property.ompV2Message as ExperienceOmpV2Result}
                                clickstreamTrigger={{
                                    triggerLocation: 'how_much_is_my_home_worth',
                                    triggerObject: 'top',
                                    triggerSource: 'how_much_is_my_home_worth|top',
                                    triggerObjectIsSecondary: false,
                                }}
                                placementName="HMIMHW_TOP_SLOT"
                                supportedComponents={{
                                    OmpV2WebUpsellCard,
                                    OmpV2WebEmphasizedUpsellCard,
                                }}
                            />
                        )}
                        <SellerLinks
                            property={property}
                            abTests={abTests}
                            setShowContactAgentForm={setShowContactAgentForm}
                            surfaceId={surfaceId}
                            placementId={placementId}
                        />
                    </React.Fragment>
                )}
            </Spacer>
            <StyledTextButton
                data-testid="close-button"
                onClick={() => {
                    setZpid(null);
                    setHmimhwAnalyticsData(null);
                    if (typeof resetRequiredCustomDimensions === 'function') {
                        resetRequiredCustomDimensions();
                    }
                }}
                icon={<StyledIconClose />}
            />
        </ResponsiveLightbox>
    );
};

/* eslint-disable zillow/@typescript-eslint/no-unsafe-assignment */
const HomeDetailLightboxContentContainer: React.ComponentType = connectToUserSession(
    (state: { user: { loggedIn: boolean } }): { loggedIn: boolean } => ({
        loggedIn: state.user.loggedIn,
    })
)(UnwrappedHomeDetailLightboxContent);

const HomeDetailLightboxContent = (props) => (
    <Provider store={createUserSessionStore()}>
        <HomeDetailLightboxContentContainer {...props} />
    </Provider>
);
/* eslint-enable zillow/@typescript-eslint/no-unsafe-assignment */

export default HomeDetailLightboxContent;
